import React from 'react';
import { ChevronDown, Search } from 'lucide-react';

/**
 * ONE SET OF APP PRIMITIVES FOR EVERY DASHBOARD SCREEN.
 *
 * Every colour here is a SEMANTIC TOKEN from index.css (--app-*), never a
 * literal hex. That is the whole point of the file: light mode is the
 * industry-standard neutral product stack (near-white canvas, white
 * surfaces, 1px neutral hairlines, near-black text) and the brand accent is
 * spent only on primary actions, focus and active state.
 *
 * Cream and espresso stay on the marketing pages. In dense product UI a
 * cream panel on a cream ground gives no value separation, which is what
 * made these screens read as beige and dated.
 *
 * DARK MODE, LATER: remap the --app-* variables under one
 * `[data-theme='dark']` block in index.css. Do not add a second palette to
 * these components, and do not reintroduce hardcoded hex — a component that
 * names a colour instead of a role is the one that will break the theme.
 */

/** Roles, for the rare case a token is needed in an inline style. */
export const APP_TOKEN = {
  canvas: 'var(--app-canvas)',
  surface: 'var(--app-surface)',
  border: 'var(--app-border)',
  text: 'var(--app-text)',
  textMuted: 'var(--app-text-muted)',
  accent: 'var(--app-accent)'
} as const;

/** Resting elevation for a surface sitting on the app canvas. */
export const SURFACE = 'rounded-xl border border-app bg-app-surface shadow-[0_1px_2px_rgba(20,20,24,0.04)]';

/**
 * Title, one line of explanation, one action. 20px radius and a 27px display
 * heading belonged to a marketing card; a product screen header is smaller
 * and quieter, because the content below it is the subject.
 */
export function ScreenHeader({
  title,
  description,
  action
}: {
  title: string;
  description?: string;
  action?: React.ReactNode;
}) {
  return <div className="flex flex-wrap items-start gap-4" data-id="element-2992" data-mp="25i3wz1l4zcb6">
      <div className="mr-auto min-w-0" data-id="element-2993" data-mp="25kqdhqq0925j">
        <h1 className="text-[20px] font-semibold tracking-[-0.01em] text-app" data-id="element-2994" data-mp="20kkiq1ofuzj8">{title}</h1>
        {description && <p className="mt-1.5 max-w-[68ch] text-[13.5px] leading-relaxed text-app-muted" data-id="element-2995" data-mp="2q8zpeq3cyt4a">
            {description}
          </p>}
      </div>
      {action}
    </div>;
}

/** The one filled control on a screen: the brand accent, used once. */
export function PrimaryAction({
  children,
  label
}: {
  children?: React.ReactNode;
  label?: string;
}) {
  return <button type="button" aria-label={label} className="inline-flex flex-none items-center gap-1.5 rounded-lg bg-app-accent px-3.5 py-2 text-[13px] font-semibold text-white transition-colors duration-200 ease-out hover:bg-[var(--app-accent-strong)]" data-id="element-2996" data-mp="345cxj8m05i63">
      {children}
    </button>;
}

/** A quiet bordered control. Neutral at rest, accent border on focus. */
export function SelectPill({
  value,
  onChange,
  options,
  label,
  prefix
}: {
  value: string;
  onChange: (value: string) => void;
  options: string[];
  label: string;
  prefix?: string;
}) {
  return <div className="relative" data-id="element-2997" data-mp="righskaijgp">
      <select value={value} onChange={event => onChange(event.target.value)} aria-label={label} className="appearance-none rounded-lg border border-app bg-app-surface py-2 pl-3 pr-8 text-[13px] font-medium text-app outline-none transition-colors duration-200 ease-out hover:border-app-strong focus:border-app-accent" data-id="element-2998" data-mp="omhs20mbqr7">
        {options.map(option => <option key={option} value={option} data-id="element-2999" data-mp="16ctx4bpsj6us">
            {prefix ? `${prefix} · ${option}` : option}
          </option>)}
      </select>
      <ChevronDown size={13} className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 text-app-subtle" data-id="element-3000" data-mp="23yr7flu0o5vn" />
    </div>;
}

/** Layout / view switcher. The active segment is the text colour, not a hue. */
export function Segmented<T extends string>({
  value,
  onChange,
  options
}: {
  value: T;
  onChange: (value: T) => void;
  options: readonly (readonly [T, React.ElementType])[];
}) {
  return <div className="flex flex-none items-center gap-0.5 rounded-lg border border-app bg-app-surface p-1" data-id="element-3001" data-mp="2ms3gp6uqs255">
      {options.map(([key, Icon]) => <button key={key} type="button" onClick={() => onChange(key)} aria-pressed={value === key} aria-label={`${key} layout`} className={`rounded-md p-1.5 transition-colors duration-200 ease-out ${value === key ? 'bg-app-text text-white' : 'text-app-subtle hover:text-app'}`} data-id="element-3002" data-mp="22gr9e4hhr5v2">
          <Icon size={14} data-id="element-3003" data-mp="1xi7om7emw04a" />
        </button>)}
    </div>;
}
export function SearchField({
  value,
  onChange,
  placeholder,
  label
}: {
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
  label: string;
}) {
  return <div className="relative" data-id="element-3004" data-mp="2q5tph7wh8qng">
      <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-app-subtle" data-id="element-3005" data-mp="2q8y12qc9uoo5" />
      <input type="search" value={value} onChange={event => onChange(event.target.value)} placeholder={placeholder} aria-label={label} className="w-full rounded-lg border border-app bg-app-surface py-2 pl-9 pr-3 text-[13.5px] text-app outline-none transition-colors duration-200 ease-out placeholder:text-app-subtle focus:border-app-accent" data-id="element-3006" data-mp="2bhp6e2tiid45" />
    </div>;
}

/**
 * A GROUPED LIST, not a grid of boxes. One surface, hairline dividers: a list
 * has one left edge to run your eye down, where a grid draws a rectangle
 * around every item and gives them all identical weight.
 */
export function Panel({
  children,
  className = ''
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return <div className={`overflow-hidden ${SURFACE} ${className}`} data-id="element-3007" data-mp="129vxl6bhu8kq">{children}</div>;
}

/** One row inside a Panel. */
export function Row({
  children,
  className = ''
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return <div className={`group flex items-center gap-3 border-b border-app-divider px-4 py-3 transition-colors duration-200 ease-out last:border-0 hover:bg-app-subtle ${className}`} data-id="element-3008" data-mp="nk7uq0nnz1uw">
      {children}
    </div>;
}

/**
 * The row's mark. Quiet by default, accented only while the row is pointed
 * at — an always-on tinted tile per row turns a list into a wall of badges.
 */
export function Glyph({
  icon: Icon
}: {
  icon: React.ElementType;
}) {
  return <span aria-hidden="true" className="flex size-8 flex-none items-center justify-center rounded-lg bg-app-subtle text-app-muted transition-colors duration-200 ease-out group-hover:bg-app-accent-tint group-hover:text-app-accent" data-id="element-3009" data-mp="rm2kas6cvkr9">
      <Icon size={15} strokeWidth={2} data-id="element-3010" data-mp="11ixe4xroowz7" />
    </span>;
}

/** Row title over one line of supporting detail. */
export function RowText({
  title,
  meta
}: {
  title: string;
  meta: React.ReactNode;
}) {
  return <span className="min-w-0 flex-1" data-id="element-3011" data-mp="8ujigoo2icqt">
      <span className="block truncate text-[13.5px] font-medium text-app" data-id="element-3012" data-mp="duo0wk8jdhjo">{title}</span>
      <span className="mt-0.5 block truncate text-[12px] text-app-muted" data-id="element-3013" data-mp="hwj09j6wpkut">{meta}</span>
    </span>;
}

/**
 * Status as a small dot, on the semantic status tokens rather than the brand
 * accent — "active" is a state, not a call to action, and colour-coding it
 * orange competed with the one control that should own the accent.
 */
export function StatusRing({
  status
}: {
  status: 'active' | 'dormant' | 'archived';
}) {
  const tone = status === 'active' ? 'bg-[var(--app-success)]' : status === 'dormant' ? 'bg-[var(--app-text-subtle)]' : 'bg-[var(--app-border-strong)]';
  return <span aria-label={status} title={status} className={`size-[7px] flex-none rounded-full ${tone}`} data-id="element-3014" data-mp="39sn34t45zmgw" />;
}

/** A number over its label. Number first — it is what is being compared. */
export function MetaStat({
  label,
  value
}: {
  label: string;
  value: React.ReactNode;
}) {
  return <div data-id="element-3015" data-mp="2zwbrm5cef0ni">
      <dd className="text-[15px] font-semibold leading-none tabular-nums text-app" data-id="element-3016" data-mp="2v3r1bdspb5oq">{value}</dd>
      <dt className="mt-1 text-[11px] text-app-subtle" data-id="element-3017" data-mp="2q4nt5khfcjcj">{label}</dt>
    </div>;
}

/** The empty state. */
export function EmptyState({
  children
}: {
  children: React.ReactNode;
}) {
  return <div className={`${SURFACE} px-6 py-14 text-center text-[13.5px] text-app-muted`} data-id="element-3018" data-mp="2m0zob9k6g1uz">{children}</div>;
}